import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EMPTY, ReplaySubject, of } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AlertService } from '@services/alert.service';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel, ImporterOfRecordContactInformationModel } from '@data-layer/models/companies';
import { UserModel } from '@data-layer/models/user';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { DigitalSignModel, DigitalSignUpdateBody, DigitalSignsRepository } from '@data-layer/repositories/digital-signs.repository';
import { BillsRepository } from '@data-layer/repositories/bills.repository';
import { BillItemPendingModel } from '@data-layer/models/payments';
import { enumCompanyBusinessType, enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { enumRespErrors } from '@data-layer/enums/common';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { IHeaderItem } from '@app/modules/app-layout/interfaces';
import { AppService } from '@app/app.service';
import { PoaIorEditSidebarComponent } from '@app/modules/company/components';
import { OrganizationMemberInviteFormComponent } from '@app/modules/organization/components/organization-member-invite-form/organization-member-invite-form.component';
import { OrganizationsService } from '@services/organizations.service';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { OrganizationModel } from '@data-layer/models/organizations';
import { GenericService } from '@services/generic.service';
import { InputPhoneComponent } from '@shared/input-phone/components';

@Component({
  selector: 'app-poa-purchase',
  templateUrl: './poa-purchase.component.html',
  styleUrls: ['./poa-purchase.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoaPurchaseComponent implements OnInit, OnDestroy {
  @ViewChild(InputPhoneComponent, { static: false }) public inputPhone: InputPhoneComponent;

  public companyId: number;
  public company: CompanyModel;
  public pendingBills: BillItemPendingModel[] = [];
  public isPaymentPending: boolean = false;
  public CUSTOM_MASK = CUSTOM_MASK;
  public enumCompanyBusinessType: typeof enumCompanyBusinessType = enumCompanyBusinessType;
  public isMobile: boolean = false;

  /** Local IOR contact data used for Case 3 read-only display and sidebar editing */
  public iorContactData: ImporterOfRecordContactInformationModel = {};

  public formGroup: UntypedFormGroup;
  public errors: any = {
    required: 'FORM_ERROR.REQUIRED',
    phoneNumber: {
      required: 'FORM_ERROR.REQUIRED',
      maxlength: 'FORM_ERROR.MAX_LENGTH',
      validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
    },
  };

  private user: UserModel;
  private digitalSign: DigitalSignModel;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  private organization: OrganizationModel;
  private returnUrl: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private companiesService: CompaniesService,
    private digitalSignsRepository: DigitalSignsRepository,
    private billsRepository: BillsRepository,
    private appLayoutService: AppLayoutService,
    private appService: AppService,
    private organizationsService: OrganizationsService,
    private organizationsRepository: OrganizationsRepository,
    private genericService: GenericService,
    private alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    this.companyId = +this.route.snapshot.paramMap.get('id');
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || `/companies/account-settings/company-view/${this.companyId}`;
    this.createForm();
    this.genericService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile: boolean) => {
      this.isMobile = isMobile;
      this.cd.markForCheck();
    });

    // Ensure company context is loaded
    this.companiesService
      .getCompanyAsync(true, true, this.companyId)
      .pipe(
        switchMap((company: CompanyModel) => {
          this.company = company;
          this.user = this.companiesService.getUser();
          return this.loadDigitalSignAndBills();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadOrganization();
        this.updateDashboardHeader();
        this.cd.markForCheck();
      });
  }

  private loadOrganization(): void {
    const user = this.companiesService.getUser();
    const orgId = user?.defaultOrganizationId || this.company?.organizationId;
    if (!orgId) {
      return;
    }
    this.organizationsService
      .getOrganizationByIdFromServer(orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((org: OrganizationModel) => {
        this.organization = org;
        this.cd.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get totalPrice(): number {
    return (this.pendingBills || []).reduce((sum, item) => sum + (item.quantity ? item.quantity * item.price : item.price), 0);
  }

  public get isHasInstantPayment(): boolean {
    return !!(this.pendingBills || []).filter((x) => x.paymentType === enumCompanyPaymentType.INSTANT).length;
  }

  /**
   * Determines which UI layout to show based on company IOR status.
   * - Case 1: IOR=true, IOR phone exists — show only positionOfChief field.
   * - Case 2: IOR=true, IOR phone missing — show positionOfChief + phoneNumber fields.
   * - Case 3: IOR=false — show positionOfChief + read-only IOR card with pencil edit sidebar.
   */
  public get displayCase(): 1 | 2 | 3 {
    if (!this.company) {
      return 1;
    }

    if (this.company.isImporterOfRecord) {
      return this.company.importerOfRecordContactInformation?.phoneNumber ? 1 : 2;
    }

    return 3;
  }

  public getCompanyAddressLine(company: CompanyModel): string {
    return this.companiesService.getCompanyAddressLine(company);
  }

  public continue(): void {
    const positionCtrl = this.formGroup.get('positionOfChief') as UntypedFormControl;
    const trimmedPosition = String(positionCtrl.value || '').trim();

    // Validate position
    if (!trimmedPosition) {
      positionCtrl.setValue(trimmedPosition);
      this.formGroup.markAllAsTouched();
      this.cd.markForCheck();
      return;
    }

    // Validate phone for Case 2 — check both required and format validation from input-phone
    if (this.displayCase === 2 && !this.isPhoneValid()) {
      this.formGroup.markAllAsTouched();
      this.inputPhone?.inputFormControl?.markAsTouched();
      this.cd.markForCheck();
      return;
    }

    const positionOfChief = trimmedPosition.slice(0, 35);
    const body = this.buildDigitalSignUpdateBody(positionOfChief);

    this.digitalSignsRepository
      .update(this.digitalSign.id, body)
      .pipe(
        switchMap(() => {
          if (this.isHasInstantPayment) {
            this.router.navigate(['payment'], {
              queryParams: { referenceNumbers: [this.digitalSign.referenceNumber], returnUrl: this.returnUrl },
            });
            return of(void 0);
          }

          // Monthly: create bills (same as bonds send flow) then go back to returnUrl
          return this.billsRepository
            .create(this.company.id, {
              referenceNumbers: [this.digitalSign.referenceNumber],
              coupon: null,
              paymentProvider: null,
              paymentCodeId: null,
            })
            .pipe(
              catchError((error) => {
                // #13269 — the string tag lives in .tag; .code is the numeric HTTP status and never matched
                if (error?.error?.tag === enumRespErrors.MissingRequiredDocuments) {
                  this.alertService.error('COMPANY.PHOTO_ID_REQUIRED_FOR_DIGITAL_SIGN');
                }
                return EMPTY;
              }),
              finalize(() => {
                this.router.navigateByUrl(this.returnUrl);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private createForm(): void {
    this.formGroup = this.fb.group({
      positionOfChief: new UntypedFormControl('', [Validators.required]),
      phoneNumber: new UntypedFormControl('', [Validators.required]),
      sendCcEmail: new UntypedFormControl(false),
    });
  }

  private isPhoneValid(): boolean {
    const phoneCtrl = this.formGroup.get('phoneNumber');
    const hasValue = !!phoneCtrl.value;
    // Check both the parent control and the internal inputFormControl from input-phone component
    const isInternalValid = this.inputPhone?.inputFormControl ? !this.inputPhone.inputFormControl.invalid : true;
    return hasValue && isInternalValid;
  }

  private loadDigitalSignAndBills() {
    return this.digitalSignsRepository.list(this.companyId).pipe(
      switchMap((items) => {
        const last = (items || []).sort((a, b) => b.id - a.id)[0];
        this.digitalSign = last;
        this.initIorContactData();
        if (this.displayCase === 3 && this.digitalSign?.ccEmail) {
          this.formGroup.get('sendCcEmail').setValue(true);
        }
        if (this.digitalSign?.positionOfChief) {
          this.formGroup.get('positionOfChief').setValue(this.digitalSign.positionOfChief);
        }

        // Pre-populate phone number from digital sign for Case 2
        if (this.displayCase === 2 && this.digitalSign?.phoneNumber) {
          this.formGroup.get('phoneNumber').setValue(this.digitalSign.phoneNumber);
        } else if (this.displayCase === 2 && this.user?.phoneNumber) {
          this.formGroup.get('phoneNumber').setValue(this.user.phoneNumber);
        }

        return this.billsRepository
          .pendingBills(this.company.id, {
            referenceNumbers: [this.digitalSign.referenceNumber],
            paymentProvider: null,
            paymentCodeId: null,
            isCalculateFee: false,
          })
          .pipe(
            tap((bills: BillItemPendingModel[]) => (this.pendingBills = bills || [])),
            catchError(() => of([] as BillItemPendingModel[])),
          );
      }),
    );
  }

  /**
   * Initialize IOR contact data from the digital sign object.
   * The digital sign is the source of truth because backend syncs IOR changes
   * to the company asynchronously — the company object may be stale.
   */
  private initIorContactData(): void {
    const ds = this.digitalSign;
    if (ds) {
      this.iorContactData = {
        firstName: ds.firstName || '',
        lastName: ds.lastName || '',
        phoneNumber: ds.phoneNumber || '',
        emailAddress: ds.email || '',
      };
    }
  }

  /**
   * Build the full 5-field body for PUT /v2/digital-signs/{id} based on the current displayCase.
   * - Case 1: positionOfChief from form; rest from IOR contact info.
   * - Case 2: positionOfChief + phoneNumber from form; firstName, lastName, email from IOR contact info.
   * - Case 3: positionOfChief from form; 4 IOR fields from local iorContactData (possibly edited via sidebar).
   */
  private buildDigitalSignUpdateBody(positionOfChief: string): DigitalSignUpdateBody {
    const ior = this.company?.importerOfRecordContactInformation;

    switch (this.displayCase) {
      case 1:
        return {
          positionOfChief,
          firstName: ior?.firstName || '',
          lastName: ior?.lastName || '',
          phoneNumber: ior?.phoneNumber || '',
          email: ior?.emailAddress || '',
        };

      case 2: {
        const phoneCtrl = this.formGroup.get('phoneNumber');
        const phoneValue = phoneCtrl?.value ? String(phoneCtrl.value).replace(/(\s|-)*/g, '') : '';
        return {
          positionOfChief,
          firstName: ior?.firstName || '',
          lastName: ior?.lastName || '',
          phoneNumber: phoneValue,
          email: ior?.emailAddress || '',
        };
      }

      case 3:
        return {
          positionOfChief,
          firstName: this.iorContactData?.firstName || '',
          lastName: this.iorContactData?.lastName || '',
          phoneNumber: this.iorContactData?.phoneNumber || '',
          email: this.iorContactData?.emailAddress || '',
          ccEmail: this.formGroup.get('sendCcEmail').value ? this.user.email : null,
        };
    }
  }

  private updateDashboardHeader(): void {
    const title: IHeaderItem[] = [];

    // Account settings root
    title.push({ key: 'MENU.ACCOUNT_SETTINGS', link: '/companies/account-settings' });

    // If multiple companies, include Importers of Record segment like the company page does
    if ((this.companiesService.getCompanies() || []).length > 1) {
      title.push({ key: 'LABELS.IMPORTERS_OF_RECORD', link: '/companies/account-settings/company-list' });
    }

    // Company name (intranslatable shows actual name) or fallback to LABELS.IMPORTER_OF_RECORD
    title.push({
      key: this.company?.businessName || 'LABELS.IMPORTER_OF_RECORD',
      link: `/companies/account-settings/company-view/${this.companyId}`,
      params: { intranslatable: !!this.company?.businessName },
    });

    // POA purchase segment
    title.push({ key: 'COMPANY.DIGITAL-SIGNATURE.POA_PURCHASE.BREADCRUMB', link: null });

    this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });
  }

  public openIorEditSidebar(): void {
    const sidebarRef: ComponentRef<PoaIorEditSidebarComponent> = this.appService.openSidebar(PoaIorEditSidebarComponent, {
      sidebarHeaderText: 'EDIT_CONTACT_INFORMATION',
    });
    sidebarRef.instance.iorData = { ...this.iorContactData };

    sidebarRef.instance.saveSource$
      .pipe(
        switchMap((updatedIor: ImporterOfRecordContactInformationModel) => {
          this.iorContactData = updatedIor;
          const positionOfChief = String(this.formGroup.get('positionOfChief').value || '')
            .trim()
            .slice(0, 35);
          const body = this.buildDigitalSignUpdateBody(positionOfChief);
          return this.digitalSignsRepository.update(this.digitalSign.id, body);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // iorContactData already updated from sidebar emission; no company refetch needed
        // because backend syncs IOR to company asynchronously
        this.appService.closeSidebar();
        this.cd.markForCheck();
      });

    sidebarRef.instance.confirmedAsUser$
      .pipe(
        switchMap(() => {
          const updatedCompany: CompanyModel = {
            ...this.company,
            isImporterOfRecord: true,
            importerOfRecordContactInformation: {
              firstName: this.user?.firstName || '',
              lastName: this.user?.lastName || '',
              phoneNumber: this.user?.phoneNumber || '',
              emailAddress: this.user?.email || '',
            },
          };
          return this.companiesService.updateCompany(updatedCompany);
        }),
        switchMap((company: CompanyModel) => {
          const positionOfChief: string = String(this.formGroup.get('positionOfChief').value || '')
            .trim()
            .slice(0, 35);
          const ior = company?.importerOfRecordContactInformation;
          const body: DigitalSignUpdateBody = {
            positionOfChief,
            firstName: ior?.firstName || '',
            lastName: ior?.lastName || '',
            phoneNumber: ior?.phoneNumber || '',
            email: ior?.emailAddress || '',
          };
          return this.digitalSignsRepository.update(this.digitalSign.id, body).pipe(map(() => company));
        }),
        catchError(() => {
          this.alertService.error('LABELS.SOMETHING_DONT_WORK_MESSAGE');
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((company: CompanyModel) => {
        this.company = company;
        this.iorContactData = { ...(company?.importerOfRecordContactInformation || {}) };
        this.formGroup.get('sendCcEmail')?.setValue(false, { emitEvent: false });
        this.appService.closeSidebar();
        this.cd.markForCheck();
      });
  }

  public onInviteMembers(): void {
    const open = () => {
      const sidebarRef = this.appService.openSidebar(OrganizationMemberInviteFormComponent, {
        sidebarHeaderText: 'LABELS.INVITE_MEMBERS',
      });
      sidebarRef.instance.selectedCompaniesIds = [this.company.id];
      sidebarRef.instance.acceptedMembers = [];
      sidebarRef.instance.currentMember = null;
      sidebarRef.instance.companies = this.organization?.companies?.length ? this.organization.companies : [this.company];

      // Wire send and update to API, mirror organization page behavior
      sidebarRef.instance.sendInviteSource
        .pipe(
          switchMap((data) => this.organizationsRepository.inviteOrganizationMember(this.organization.id, data)),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.appService.closeSidebar();
          this.cd.markForCheck();
        });

      sidebarRef.instance.updateInviteSource
        .pipe(
          switchMap((response: any) =>
            this.organizationsRepository.updateOrganizationMember(this.organization.id, response.member.user.userId, response.data),
          ),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.appService.closeSidebar();
          this.cd.markForCheck();
        });
    };

    if (!this.organization) {
      const user = this.companiesService.getUser();
      const orgId = user?.defaultOrganizationId || this.company?.organizationId;
      if (!orgId) {
        open();
        return;
      }
      this.organizationsService
        .getOrganizationByIdFromServer(orgId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => open());
    } else {
      open();
    }
  }
}
